import React, { useEffect, useRef, useState } from 'react';
import authApi, { ChatMessageResponse } from '../services/authApi';
import { useAuth } from '../context/AuthContext';
import { useChatBadge } from '../context/ChatBadgeContext';
import './ChatWidget.css';

const POLL_INTERVAL = 30000;

const ChatWidget: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { unread, setUnread } = useChatBadge();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!open || !isAuthenticated) return;
    loadMessages();
    pollRef.current = setInterval(loadMessages, POLL_INTERVAL);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [open, isAuthenticated]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const loadMessages = async () => {
    try {
      const res = await authApi.getChatMessages();
      setMessages(res.data ?? []);
      setUnread(0);
    } catch {}
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await authApi.sendChatMessage(input.trim());
      setMessages((prev) => [...prev, res.data]);
      setInput('');
    } catch {} finally {
      setSending(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="cw-root">
      {open && (
        <div className="cw-panel">
          <div className="cw-header">
            <div className="cw-header-info">
              <div className="cw-avatar">S</div>
              <div>
                <div className="cw-header-name">SwingSetup Support</div>
                <div className="cw-header-sub">We reply within a few hours</div>
              </div>
            </div>
            <button className="cw-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="cw-messages">
            {messages.length === 0 && (
              <div className="cw-empty">
                👋 Hi {user?.name?.split(' ')[0] || 'there'}! Ask us anything about the learning or your subscription.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`cw-msg ${m.senderType === 'USER' ? 'cw-msg--user' : 'cw-msg--admin'}`}>
                <div className="cw-bubble">{m.content}</div>
                <div className="cw-time">{formatTime(m.createdAt)}</div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="cw-input-row">
            <textarea
              className="cw-input"
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
            />
            <button className="cw-send" onClick={send} disabled={sending || !input.trim()}>
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      <button className="cw-fab" onClick={() => setOpen((o) => !o)} aria-label="Chat with us">
        {open ? <CloseIcon /> : <ChatIcon />}
        {!open && unread > 0 && <span className="cw-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
    </div>
  );
};

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
    ' · ' + d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="20" height="20">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

export default ChatWidget;
